import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type { TaskPlan, ActionItem, ActionItemStatus, TaskPlanStatus, TodoSwimLane } from '../types';
import { isValidDateKey } from '../utils/todoSwimLanes';

const paths = {
  tasks: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'tasks'),
  task: (userId: string, taskId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'tasks', taskId),
};

function getActionItemStatus(item: ActionItem): ActionItemStatus {
  if (item.status) return item.status;
  return item.completed ? 'done' : 'todo';
}

function isActionItemDone(item: ActionItem): boolean {
  return getActionItemStatus(item) === 'done';
}

function normalizeActionItem(rawItem: unknown, fallbackIndex: number): ActionItem | null {
  if (!rawItem || typeof rawItem !== 'object') return null;
  const record = rawItem as Record<string, unknown>;

  const rawText = record.text;
  const text = typeof rawText === 'string'
    ? rawText.trim()
    : (rawText && typeof rawText === 'object' && typeof (rawText as Record<string, unknown>).text === 'string')
      ? String((rawText as Record<string, unknown>).text).trim()
      : '';
  if (!text) return null;

  const status: ActionItemStatus = record.status === 'in_progress'
    ? 'in_progress'
    : record.status === 'done'
      ? 'done'
      : 'todo';
  const completed = status === 'done' || Boolean(record.completed);
  const id = typeof record.id === 'string' && record.id.trim().length > 0
    ? record.id
    : `legacy-action-${fallbackIndex}`;
  const order = Number.isFinite(Number(record.order)) ? Number(record.order) : fallbackIndex;
  const completedAt = completed && record.completedAt ? (record.completedAt as ActionItem['completedAt']) : undefined;
  const parentItemId = typeof record.parentItemId === 'string' && record.parentItemId.trim().length > 0
    ? record.parentItemId
    : undefined;

  return {
    id,
    text,
    completed,
    status,
    completedAt,
    order,
    parentItemId,
  };
}

export function normalizeTaskPlan(id: string, rawData: unknown): TaskPlan {
  const data = (rawData && typeof rawData === 'object') ? rawData as Record<string, unknown> : {};
  const rawItems = Array.isArray(data.actionItems) ? data.actionItems : [];
  const normalizedItems = rawItems
    .map((item, index) => normalizeActionItem(item, index))
    .filter((item): item is ActionItem => item !== null)
    .sort((a, b) => a.order - b.order)
    .map((item, index) => ({ ...item, order: index }));

  const rawLane = data.swimLane;
  const swimLane: TodoSwimLane | undefined =
    rawLane === 'today' || rawLane === 'this_week' || rawLane === 'later' ? rawLane : undefined;

  const projectId = typeof data.projectId === 'string' && data.projectId.trim().length > 0
    ? data.projectId
    : undefined;
  const dueDate = isValidDateKey(data.dueDate) ? data.dueDate : undefined;

  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : '',
    title: typeof data.title === 'string' ? data.title : 'Untitled task',
    summary: typeof data.summary === 'string' ? data.summary : '',
    status: data.status === 'completed' || data.status === 'abandoned' ? data.status : 'active',
    actionItems: normalizedItems,
    swimLane,
    projectId,
    dueDate,
    createdAt: data.createdAt as TaskPlan['createdAt'],
    updatedAt: data.updatedAt as TaskPlan['updatedAt'],
    completedAt: data.completedAt as TaskPlan['completedAt'],
  };
}

export async function createTaskPlan(
  userId: string,
  sessionId: string,
  title: string,
  summary: string,
  actionItems: Omit<ActionItem, 'completed' | 'completedAt'>[]
): Promise<string> {
  const now = Timestamp.now();
  const items: ActionItem[] = actionItems.map((item) => ({
    ...item,
    completed: false,
    status: 'todo',
  }));

  const taskData: Omit<TaskPlan, 'id'> = {
    userId,
    sessionId,
    title,
    summary,
    status: 'active',
    actionItems: items,
    createdAt: now,
    updatedAt: now,
  };

  const ref = await addDoc(paths.tasks(userId), taskData);
  return ref.id;
}

export async function getTaskPlan(userId: string, taskId: string): Promise<TaskPlan | null> {
  const snap = await getDoc(paths.task(userId, taskId));
  if (!snap.exists()) return null;
  return normalizeTaskPlan(snap.id, snap.data());
}

export async function getActiveTasks(userId: string): Promise<TaskPlan[]> {
  const q = query(
    paths.tasks(userId),
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => normalizeTaskPlan(d.id, d.data()));
}

export async function getAllTasks(userId: string): Promise<TaskPlan[]> {
  const q = query(paths.tasks(userId), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => normalizeTaskPlan(d.id, d.data()));
}

export async function toggleActionItem(
  userId: string,
  taskId: string,
  actionItemId: string,
  completed: boolean
): Promise<void> {
  const task = await getTaskPlan(userId, taskId);
  if (!task) return;
  const now = Timestamp.now();

  const updatedItems = task.actionItems.map((item) => {
    if (item.id !== actionItemId) return item;
    const { completedAt: _completedAt, ...rest } = item;
    if (completed) {
      const next: ActionItem = { ...rest, status: 'done', completed: true, completedAt: now };
      return next;
    }
    const next: ActionItem = { ...rest, status: 'todo', completed: false };
    return next;
  });

  const allComplete = updatedItems.length > 0 && updatedItems.every((item) => isActionItemDone(item));

  const updates: Record<string, unknown> = {
    actionItems: updatedItems,
    updatedAt: Timestamp.now(),
  };

  if (allComplete) {
    updates.status = 'completed' satisfies TaskPlanStatus;
    updates.completedAt = Timestamp.now();
  } else if (task.status === 'completed') {
    updates.status = 'active' satisfies TaskPlanStatus;
    updates.completedAt = null;
  }

  await updateDoc(paths.task(userId, taskId), updates);
}

export async function updateTaskStatus(
  userId: string,
  taskId: string,
  status: TaskPlanStatus
): Promise<void> {
  const updates: Record<string, unknown> = {
    status,
    updatedAt: Timestamp.now(),
  };
  if (status === 'completed') {
    updates.completedAt = Timestamp.now();
  }
  await updateDoc(paths.task(userId, taskId), updates);
}

export async function deleteActionItem(
  userId: string,
  taskId: string,
  actionItemId: string
): Promise<void> {
  const task = await getTaskPlan(userId, taskId);
  if (!task) return;

  const filtered = task.actionItems.filter((item) => item.id !== actionItemId);

  const updates: Record<string, unknown> = {
    actionItems: filtered,
    updatedAt: Timestamp.now(),
  };

  const allComplete = filtered.length > 0 && filtered.every((item) => isActionItemDone(item));
  if (allComplete) {
    updates.status = 'completed' satisfies TaskPlanStatus;
    updates.completedAt = Timestamp.now();
  }

  await updateDoc(paths.task(userId, taskId), updates);
}

export async function addActionItem(
  userId: string,
  taskId: string,
  text: string,
  options?: { parentItemId?: string | null; afterItemId?: string }
): Promise<string | null> {
  const task = await getTaskPlan(userId, taskId);
  if (!task) return null;

  const maxOrder = task.actionItems.reduce((max, item) => Math.max(max, item.order), -1);
  const newItem: ActionItem = {
    id: `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    text,
    completed: false,
    status: 'todo',
    order: maxOrder + 1,
    ...(options?.parentItemId ? { parentItemId: options.parentItemId } : {}),
  };

  // If `afterItemId` is provided, splice the new bullet directly after it so
  // outline-style "Enter creates next sibling" feels right; otherwise append.
  let nextItems: ActionItem[];
  if (options?.afterItemId) {
    const idx = task.actionItems.findIndex((it) => it.id === options.afterItemId);
    if (idx >= 0) {
      nextItems = [
        ...task.actionItems.slice(0, idx + 1),
        newItem,
        ...task.actionItems.slice(idx + 1),
      ].map((it, i) => ({ ...it, order: i }));
    } else {
      nextItems = [...task.actionItems, newItem];
    }
  } else {
    nextItems = [...task.actionItems, newItem];
  }

  await updateDoc(paths.task(userId, taskId), {
    actionItems: nextItems,
    updatedAt: Timestamp.now(),
  });
  return newItem.id;
}

/**
 * Rename a single action item in-place. Used by the bullets outline view to
 * commit inline edits without going through reorder.
 */
export async function renameActionItem(
  userId: string,
  taskId: string,
  actionItemId: string,
  text: string
): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) {
    await deleteActionItem(userId, taskId, actionItemId);
    return;
  }
  const task = await getTaskPlan(userId, taskId);
  if (!task) return;
  const next = task.actionItems.map((item) =>
    item.id === actionItemId ? { ...item, text: trimmed } : item
  );
  await updateDoc(paths.task(userId, taskId), {
    actionItems: next,
    updatedAt: Timestamp.now(),
  });
}

export async function reorderActionItems(
  userId: string,
  taskId: string,
  orderedItemIds: string[],
  statusByItemId?: Partial<Record<string, ActionItemStatus>>,
  /**
   * Optional patch to apply alongside reorder. Use `null` to clear the
   * parentItemId (promote to top-level inside the card). Items not present
   * in the map keep their existing parentItemId. The outline view uses this
   * to do indent + reorder in a single write.
   */
  parentByItemId?: Partial<Record<string, string | null>>,
): Promise<void> {
  const task = await getTaskPlan(userId, taskId);
  if (!task) return;

  const itemMap = new Map(task.actionItems.map((item) => [item.id, item]));
  const now = Timestamp.now();
  const reordered = orderedItemIds
    .map((id, index) => {
      const item = itemMap.get(id);
      if (!item) return null;
      const { completedAt: _completedAt, parentItemId: prevParent, ...rest } = item;
      const nextStatus = statusByItemId?.[id] ?? getActionItemStatus(item);

      let nextParent: string | undefined = prevParent;
      if (parentByItemId && Object.prototype.hasOwnProperty.call(parentByItemId, id)) {
        const patch = parentByItemId[id];
        nextParent = patch === null || patch === undefined ? undefined : patch;
      }
      const parentField = nextParent ? { parentItemId: nextParent } : {};

      const isDone = nextStatus === 'done';
      if (isDone) {
        const next: ActionItem = {
          ...rest,
          ...parentField,
          order: index,
          status: nextStatus,
          completed: true,
          completedAt: item.completedAt ?? now,
        };
        return next;
      }
      const next: ActionItem = {
        ...rest,
        ...parentField,
        order: index,
        status: nextStatus,
        completed: false,
      };
      return next;
    })
    .filter((item): item is ActionItem => item !== null);

  const allComplete = reordered.length > 0 && reordered.every((item) => isActionItemDone(item));
  const updates: Record<string, unknown> = {
    actionItems: reordered,
    updatedAt: Timestamp.now(),
  };
  if (allComplete) {
    updates.status = 'completed' satisfies TaskPlanStatus;
    updates.completedAt = now;
  } else if (task.status === 'completed') {
    updates.status = 'active' satisfies TaskPlanStatus;
    updates.completedAt = null;
  }

  await updateDoc(paths.task(userId, taskId), updates);
}

export async function deleteTaskPlan(userId: string, taskId: string): Promise<void> {
  await deleteDoc(paths.task(userId, taskId));
}

/** Rename a `TaskPlan` title. Used by the bullets outline view to commit inline edits on the top-level row. */
export async function updateTaskTitle(
  userId: string,
  taskId: string,
  title: string,
): Promise<void> {
  const trimmed = title.trim();
  if (!trimmed) return; // Empty trims are caller-responsible (delete vs rename).
  await updateDoc(paths.task(userId, taskId), {
    title: trimmed,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Set or clear an item's `parentItemId` inside the same `TaskPlan`. Used by
 * the bullets outline (Tab / Shift+Tab + drag-to-indent). Pass `null` to
 * outdent back to top-level under the card. Cycles are guarded against.
 */
export async function setActionItemParent(
  userId: string,
  taskId: string,
  actionItemId: string,
  parentItemId: string | null,
): Promise<void> {
  if (parentItemId === actionItemId) return;
  const task = await getTaskPlan(userId, taskId);
  if (!task) return;

  // Cycle guard: walk parent chain and refuse if we'd create a loop.
  if (parentItemId) {
    const byId = new Map(task.actionItems.map((it) => [it.id, it] as const));
    let cursor: string | undefined = parentItemId;
    const seen = new Set<string>();
    while (cursor) {
      if (cursor === actionItemId) return;
      if (seen.has(cursor)) break;
      seen.add(cursor);
      cursor = byId.get(cursor)?.parentItemId;
    }
  }

  const next = task.actionItems.map((item) => {
    if (item.id !== actionItemId) return item;
    if (parentItemId === null || parentItemId === undefined) {
      const { parentItemId: _drop, ...rest } = item;
      return rest;
    }
    return { ...item, parentItemId };
  });

  await updateDoc(paths.task(userId, taskId), {
    actionItems: next,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Create a brand-new lightweight `TaskPlan` for a free-typed bullet on the
 * outline view (no session attached). Used by the per-lane "+ new bullet"
 * composer so manual top-level lines have a single source of truth shared
 * with List / Kanban / Projects.
 */
export async function createBulletTaskPlan(
  userId: string,
  title: string,
  options: { swimLane?: TodoSwimLane | null; projectId?: string | null } = {},
): Promise<string> {
  const trimmed = title.trim();
  if (!trimmed) throw new Error('Bullet text cannot be empty.');

  const now = Timestamp.now();
  const data: Record<string, unknown> = {
    userId,
    sessionId: '',
    title: trimmed,
    summary: '',
    status: 'active' satisfies TaskPlanStatus,
    actionItems: [] as ActionItem[],
    createdAt: now,
    updatedAt: now,
  };
  if (options.swimLane) data.swimLane = options.swimLane;
  if (options.projectId) data.projectId = options.projectId;

  const ref = await addDoc(paths.tasks(userId), data);
  return ref.id;
}

/* `createSplitTaskPlan` was removed: it created Firestore duplicates when
 * the user dragged a sub-task across columns. The correct model is "one
 * TaskPlan, projected into multiple visual columns by its action items'
 * statuses" — see the `rowsByColumn` projection in `ProjectsPivot.tsx`.
 * The data model stays unified; only the UI splits. */

export async function updateTaskSwimLane(
  userId: string,
  taskId: string,
  swimLane: TodoSwimLane | null
): Promise<void> {
  await updateDoc(paths.task(userId, taskId), {
    // Pass `null` to remove the field — used by the Unschedule drop zone so
    // the card stops appearing in any lane while keeping its project intact.
    swimLane: swimLane ?? deleteField(),
    updatedAt: Timestamp.now(),
  });
}

/** Assign or clear the project on a task. Pass `null` to move it to Inbox. */
export async function updateTaskProject(
  userId: string,
  taskId: string,
  projectId: string | null
): Promise<void> {
  await updateDoc(paths.task(userId, taskId), {
    projectId: projectId ?? deleteField(),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Set or clear `dueDate` (`YYYY-MM-DD`). Validates strictly to avoid malformed Firestore writes
 * (PRD §8.2).
 */
export async function updateTaskDueDate(
  userId: string,
  taskId: string,
  dueDate: string | null
): Promise<void> {
  if (dueDate !== null && !isValidDateKey(dueDate)) {
    throw new Error('dueDate must be in YYYY-MM-DD format.');
  }
  await updateDoc(paths.task(userId, taskId), {
    dueDate: dueDate ?? deleteField(),
    updatedAt: Timestamp.now(),
  });
}
